import {DEFAULT_ITEM_QUERY_PARAMS, ItemsQueryParams, ListBundlesParams} from './clare';
import {FactoryFn} from './factory-function';

export interface ProductQueryParams {
  page: number;
  take?: number;
  query?: string;
  status?: ProductStatusFilter;
  active?: boolean;
  catalogId?: number;
  sortBy?: string;
  direction?: SortDirection;
  exclude_catalog_id?: number;
  preventNavigation?: boolean;
}

export interface ProductQueryParamsInput extends Omit<
  Partial<ProductQueryParams>,
  'page' | 'take' | 'status' | 'active' | 'catalogId'
> {
  page?: number | string | null;
  take?: number | string | null;
  status?: string | null;
  active?: boolean | string | null;
  catalogId?: number | string | null;
}

export type SortDirection = 'asc' | 'desc';
export type ProductStatusFilter = 'active' | 'inactive';

export const defaultProductQueryParams = (): ProductQueryParams => {
  return {
    page: 1,
    take: 10,
  };
};

const normalizePositiveInteger = (value: number | string | null | undefined, fallback: number): number => {
  const normalized = typeof value === 'string' ? Number(value) : value;

  if (typeof normalized === 'number' && Number.isFinite(normalized) && normalized > 0) {
    return Math.floor(normalized);
  }

  return fallback;
};

const normalizeBoolean = (value: boolean | string | null | undefined): boolean | undefined => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  return undefined;
};

const normalizeStatus = (value: string | null | undefined): ProductStatusFilter | undefined => {
  return value === 'active' || value === 'inactive' ? value : undefined;
};

const statusToActive = (status: ProductStatusFilter | undefined): boolean | undefined => {
  if (status === 'active') {
    return true;
  }

  if (status === 'inactive') {
    return false;
  }

  return undefined;
};

// DWB [2026-07-24] ECOMM-5234: This function normalizes URL-style query params before they are reused.
// Router params can arrive as strings. `status` is the canonical URL field while `active` is the API
// field, so page/take/status/active are handled explicitly below rather than copied through from the raw input.
export const normalizeProductQueryParams = (src: ProductQueryParamsInput = {}): ProductQueryParams => {
  const defaultParams = defaultProductQueryParams();
  const status = normalizeStatus(src.status);
  const active = statusToActive(status) ?? normalizeBoolean(src.active);
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const {page, take, query, sortBy, direction, exclude_catalog_id, preventNavigation} = src;
  const catalogId = normalizePositiveInteger(src.catalogId, 0);
  const params: ProductQueryParams = {
    ...defaultParams,
    page: normalizePositiveInteger(page, defaultParams.page),
    take: normalizePositiveInteger(take, defaultParams.take ?? 10),
  };

  if (status !== undefined) {
    params.status = status;
  }
  if (active !== undefined) {
    params.active = active;
  }
  if (catalogId > 0) {
    params.catalogId = catalogId;
  }
  if (query !== undefined) {
    params.query = query;
  }
  if (sortBy !== undefined) {
    params.sortBy = sortBy;
  }
  if (direction !== undefined) {
    params.direction = direction;
  }
  if (exclude_catalog_id !== undefined) {
    params.exclude_catalog_id = exclude_catalog_id;
  }
  if (preventNavigation !== undefined) {
    params.preventNavigation = preventNavigation;
  }

  return params;
};

export const productQueryParamsToItemsQueryParams: FactoryFn<ProductQueryParams, ItemsQueryParams> = (
  src: ProductQueryParams
): ItemsQueryParams => {
  const normalizedSrc = normalizeProductQueryParams(src);
  const result: ItemsQueryParams = {
    ...DEFAULT_ITEM_QUERY_PARAMS,
    page: normalizedSrc.page,
    per_page: normalizedSrc.take || 10,
  };
  if (normalizedSrc.query) {
    result.by_name = String(normalizedSrc.query);
  }
  if (typeof normalizedSrc.active === 'boolean') {
    result.active = normalizedSrc.active;
  }
  if (normalizedSrc.sortBy) {
    result.order_by = listSortByToOrderBy(normalizedSrc.sortBy);
    result.direction = normalizedSrc.direction;
  }
  if (normalizedSrc.exclude_catalog_id) {
    result.exclude_catalog_id = normalizedSrc.exclude_catalog_id;
  }

  return result;
};

const SORT_BY_TO_ORDER_BY: Record<string, string> = {
  price: 'price_cents',
};

export const listSortByToOrderBy = (sortBy: string): string => {
  const normalized = sortBy.toLowerCase();
  return SORT_BY_TO_ORDER_BY[normalized] ?? normalized;
};

export const productQueryParamsToListBundlesParams = (
  src: ProductQueryParams,
  catalogId?: number,
  organizationId?: string
): ListBundlesParams => {
  const normalizedSrc = normalizeProductQueryParams(src);
  const result: ListBundlesParams = {
    page: normalizedSrc.page,
    per_page: normalizedSrc.take || 10,
  };

  if (organizationId) {
    result.organization_id = organizationId;
  }
  if (catalogId) {
    result.catalog_id = catalogId;
  }
  if (normalizedSrc.exclude_catalog_id) {
    result.exclude_catalog_id = normalizedSrc.exclude_catalog_id;
  }
  if (normalizedSrc.query) {
    result.by_name = String(normalizedSrc.query);
  }
  if (typeof normalizedSrc.active === 'boolean') {
    result.active = normalizedSrc.active;
  }
  if (normalizedSrc.sortBy) {
    result.order_by = listSortByToOrderBy(normalizedSrc.sortBy);
    result.direction = normalizedSrc.direction;
  }

  return result;
};
